import { useCallback, useMemo, useRef, useState } from 'react';

import { useAuthInfo } from '@propelauth/react';
import AwsS3 from '@uppy/aws-s3';
import Uppy, { UppyFile } from '@uppy/core';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

import { KIM_ALLOWED_FILE_TYPES, KIM_MAX_FILE_SIZE, KIM_MAX_NUMBER_OF_FILES } from '../utils/upload-constants';
import { APIBaseChronos } from '@/api/hosts';

export interface KimUploadedFile {
  id: string;
  name: string;
  extension: string;
  size: number;
  uploadId: string;
}

export function useKimFileUpload(matterId?: string) {
  const authInfo = useAuthInfo();
  const [uploadedFiles, setUploadedFiles] = useState<KimUploadedFile[]>([]);
  const uploadedFilesRef = useRef(uploadedFiles);
  uploadedFilesRef.current = uploadedFiles;
  const [isUploading, setIsUploading] = useState(false);
  const [uploadId] = useState(() => uuidv4());

  const fileIdToOriginalName = useMemo(() => new Map<string, string>(), []);

  async function getAccessToken() {
    return await authInfo.tokens.getAccessToken();
  }

  const [uppy] = useState(() => {
    const instance = new Uppy({
      restrictions: {
        maxFileSize: KIM_MAX_FILE_SIZE,
        maxNumberOfFiles: KIM_MAX_NUMBER_OF_FILES,
        allowedFileTypes: KIM_ALLOWED_FILE_TYPES,
      },
      autoProceed: true,
      onBeforeFileAdded: (file) => {
        if (!file.name) return;

        const id = uuidv4();
        const extension = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : '';
        const s3Name = `${id}${extension ? `.${extension}` : ''}`;

        fileIdToOriginalName.set(id, file.name);

        const newFile = new File([file.data], s3Name, { type: file.data.type });
        return { ...file, data: newFile, name: s3Name, meta: { ...file.meta, name: s3Name } };
      },
    }).use(AwsS3, {
      shouldUseMultipart: false,
      getUploadParameters: async () => {
        const accessToken = await getAccessToken();
        const response = await fetch(`${APIBaseChronos}/api/storage/kim/presigned-post?matterId=${matterId}&uploadId=${uploadId}`, {
          method: 'GET',
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!response.ok) throw new Error('Failed to get presigned post for Kim upload');
        return response.json();
      },
    });

    instance.on('upload', () => {
      setIsUploading(true);
    });

    instance.on('upload-success', (file: UppyFile<{}, {}> | undefined) => {
      if (!file?.name) return;
      const id = file.name.split('.')[0];
      const extension = file.name.includes('.') ? file.name.split('.').pop()! : '';
      const originalName = fileIdToOriginalName.get(id) ?? file.name;

      setUploadedFiles((prev) => [...prev, { id, name: originalName, extension, size: file.size ?? 0, uploadId }]);
    });

    instance.on('complete', () => {
      setIsUploading(false);
      instance.cancelAll();
    });

    instance.on('error', () => {
      setIsUploading(false);
      toast.error('Error uploading file');
    });

    instance.on('restriction-failed', (file: UppyFile<{}, {}> | undefined, error: Error) => {
      toast.error(error.message);
    });

    return instance;
  });

  const addFiles = useCallback(
    (files: File[]) => {
      const remaining = KIM_MAX_NUMBER_OF_FILES - uploadedFilesRef.current.length;
      if (remaining <= 0) {
        toast.error(`Maximum ${KIM_MAX_NUMBER_OF_FILES} files allowed.`);
        return;
      }

      const filesToAdd = files.slice(0, remaining);
      if (filesToAdd.length < files.length) {
        toast.error(`Maximum ${KIM_MAX_NUMBER_OF_FILES} files allowed. Only ${filesToAdd.length} file(s) added.`);
      }

      uppy.addFiles(filesToAdd.map((file) => ({ source: 'local', data: file, name: file.name })));
    },
    [uppy],
  );

  const removeFile = useCallback(
    (fileId: string) => {
      const file = uploadedFiles.find((f) => f.id === fileId);
      setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
      fileIdToOriginalName.delete(fileId);

      // Delete from S3 in the background
      if (file) {
        getAccessToken().then((accessToken) => {
          const params = new URLSearchParams({
            matterId: matterId ?? '',
            uploadId: file.uploadId,
            fileId: file.id,
            extension: file.extension,
          });
          fetch(`${APIBaseChronos}/api/storage/kim/file?${params}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${accessToken}` },
          }).catch(() => {
            console.error('Failed to delete file from S3');
          });
        });
      }
    },
    [fileIdToOriginalName, uploadedFiles, matterId],
  );

  const clearFiles = useCallback(() => {
    setUploadedFiles([]);
    fileIdToOriginalName.clear();
  }, [fileIdToOriginalName]);

  return {
    uppy,
    uploadedFiles,
    isUploading,
    addFiles,
    removeFile,
    clearFiles,
  };
}
